import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Autocomplete, Box, Button, Card, CardContent, CardHeader, IconButton, MenuItem, Stack, Tab, Tabs, TextField, Typography} from "@mui/material";
import {useDispatch, useSelector} from "react-redux";
import CategorySelect from "../input/CategorySelect";
import {fetchCategories, selectCategories} from "../../store/categorySlice";
import {fetchWalletsAndGroups, selectActiveWalletsOrSelected} from "../../store/walletSlice";
import {fetchBudgets, selectBudgetsOrSelected} from "../../store/budgetSlice";
import {Delete} from "@mui/icons-material";
import {useTranslation} from "react-i18next";

const PRESET_TYPES = [
    {value: "CUSTOM", labelKey: "reports.filters.periodTypes.custom"},
    {value: "WEEK", labelKey: "reports.filters.periodTypes.week"},
    {value: "MONTH", labelKey: "reports.filters.periodTypes.month"}
];

const RANGE_OPTIONS = [
    {value: "CURRENT", labelKey: "reports.filters.ranges.current", offset: 0},
    {value: "PREVIOUS", labelKey: "reports.filters.ranges.previous", offset: 1},
    {value: "TWO_AGO", labelKey: "reports.filters.ranges.twoAgo", offset: 2},
    {value: "THREE_AGO", labelKey: "reports.filters.ranges.threeAgo", offset: 3}
];

const MONTH_RANGE_OPTIONS = [
    ...RANGE_OPTIONS,
    {value: "PREVIOUS_YEAR", labelKey: "reports.filters.ranges.previousYear"}
];

const getRangeOffset = (rangeValue) => {
    const match = RANGE_OPTIONS.find((option) => option.value === rangeValue);
    return match ? match.offset : 0;
};

const pad2 = (value) => String(value).padStart(2, "0");
const formatDate = (date) =>
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const parseDate = (value) => {
    if (!value) return null;
    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
};

const addDays = (date, days) => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
};

const addMonths = (date, months) => {
    const next = new Date(date);
    const day = next.getDate();
    next.setDate(1);
    next.setMonth(next.getMonth() + months);
    const daysInMonth = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
    next.setDate(Math.min(day, daysInMonth));
    return next;
};

const startOfWeek = (date) => {
    const day = date.getDay();
    const diff = (day + 6) % 7;
    return addDays(date, -diff);
};

const getWeekRange = (offsetWeeks) => {
    const base = addDays(new Date(), -offsetWeeks * 7);
    const start = startOfWeek(base);
    const end = addDays(start, 6);
    return {dateFrom: formatDate(start), dateTo: formatDate(end)};
};

const getMonthRange = (offsetMonths, offsetYears = 0) => {
    const base = new Date();
    const year = base.getFullYear() + offsetYears;
    const month = base.getMonth() - offsetMonths;
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0);
    return {dateFrom: formatDate(start), dateTo: formatDate(end)};
};

const getMonthRangeForRange = (rangeValue, offsetYears = 0) => {
    const offset = getRangeOffset(rangeValue);
    return getMonthRange(offset, offsetYears);
};

const getPresetDates = (presetType, presetRange) => {
    const rangeOption = RANGE_OPTIONS.find((option) => option.value === presetRange);
    if (presetType === "WEEK") {
        const offset = rangeOption ? rangeOption.offset : 0;
        return getWeekRange(offset);
    }
    if (presetType === "MONTH") {
        const offset = rangeOption ? rangeOption.offset : 0;
        return getMonthRange(offset, 0);
    }
    return {dateFrom: "", dateTo: ""};
};

const collectDescendantIds = (categories = [], selectedIds = []) => {
    if (!selectedIds.length) return [];
    const byParent = new Map();
    categories.forEach((category) => {
        const parentId = category.parentId || null;
        if (!byParent.has(parentId)) {
            byParent.set(parentId, []);
        }
        byParent.get(parentId).push(category);
    });

    const result = new Set(selectedIds);
    const stack = [...selectedIds];
    while (stack.length) {
        const currentId = stack.pop();
        const children = byParent.get(currentId) || [];
        children.forEach((child) => {
            if (!result.has(child.id)) {
                result.add(child.id);
                stack.push(child.id);
            }
        });
    }
    return Array.from(result);
};

const ReportFilters = ({
    categoryType,
    initialValue,
    onApply,
    onReset,
    reportType: reportTypeProp,
    onReportTypeChange,
    showTypeTabs = true,
    showCategories = true,
    includeReportTypeFilter = true,
    showWallets = true,
    showBudgets = true
}) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const categories = useSelector(selectCategories);
    const wallets = useSelector((state) => selectActiveWalletsOrSelected(state, null));
    const budgets = useSelector((state) => selectBudgetsOrSelected(state, null));

    const [reportTypeState, setReportTypeState] = useState(() => {
        const initialTypes = initialValue?.filter?.types;
        return Array.isArray(initialTypes) && initialTypes.length > 0 ? initialTypes[0] : "EXPENSE";
    });
    const reportType = reportTypeProp ?? reportTypeState;
    const [presetType, setPresetType] = useState(() => {
        const initialPeriods = initialValue?.periods;
        return Array.isArray(initialPeriods) && initialPeriods.length > 0 ? "CUSTOM" : "WEEK";
    });
    const [periods, setPeriods] = useState(() => {
        const initialPeriods = initialValue?.periods;
        if (Array.isArray(initialPeriods) && initialPeriods.length > 0) {
            return initialPeriods.map((period) => ({
                dateFrom: period?.dateFrom || "",
                dateTo: period?.dateTo || "",
                presetRange: null
            }));
        }
        const defaultDates = getPresetDates("WEEK", "CURRENT");
        return [{...defaultDates, presetRange: "CURRENT"}];
    });
    const [selectedCategoryIds, setSelectedCategoryIds] = useState(initialValue?.categoryIds || []);
    const [selectedWalletIds, setSelectedWalletIds] = useState(initialValue?.walletIds || []);
    const [selectedBudgetIds, setSelectedBudgetIds] = useState(initialValue?.budgetIds || []);

    useEffect(() => {
        dispatch(fetchCategories());
        dispatch(fetchWalletsAndGroups());
        dispatch(fetchBudgets());
    }, [dispatch]);

    const typedCategories = useMemo(() => {
        const typeFilter = categoryType || (includeReportTypeFilter ? reportType : null);
        return typeFilter
            ? (categories || []).filter((category) => category.type === typeFilter)
            : (categories || []);
    }, [categories, categoryType, includeReportTypeFilter, reportType]);
    const selectedWallets = useMemo(
        () => (wallets || []).filter((wallet) => selectedWalletIds.includes(wallet.id)),
        [wallets, selectedWalletIds]
    );
    const selectedBudgets = useMemo(
        () => (budgets || []).filter((budget) => selectedBudgetIds.includes(budget.id)),
        [budgets, selectedBudgetIds]
    );

    const handleApply = useCallback(() => {
        const expandedCategoryIds = showCategories
            ? collectDescendantIds(typedCategories, selectedCategoryIds)
            : [];
        const hasTypeFilter = includeReportTypeFilter && reportType;
        const hasWallets = showWallets && selectedWalletIds.length;
        const hasBudgets = showBudgets && selectedBudgetIds.length;
        const hasFilters = expandedCategoryIds.length || hasWallets || hasBudgets || hasTypeFilter;
        const filter = hasFilters ? {
            categoryIds: showCategories ? expandedCategoryIds : null,
            walletIds: showWallets ? selectedWalletIds : null,
            budgetIds: showBudgets ? selectedBudgetIds : null,
            types: hasTypeFilter ? [reportType] : null
        } : null;
        const normalizedPeriods = (periods || [])
            .map((period) => ({
                dateFrom: period.dateFrom || null,
                dateTo: period.dateTo || null
            }))
            .filter((period) => period.dateFrom || period.dateTo);
        const requestPeriods = normalizedPeriods.length ? normalizedPeriods : null;

        if (onApply) {
            onApply({periods: requestPeriods, filter});
        }
    }, [
        includeReportTypeFilter,
        onApply,
        periods,
        reportType,
        selectedBudgetIds,
        selectedCategoryIds,
        selectedWalletIds,
        showCategories,
        typedCategories
    ]);

    const handleReset = useCallback(() => {
        if (reportTypeProp === undefined) {
            setReportTypeState("EXPENSE");
        } else if (onReportTypeChange) {
            onReportTypeChange("EXPENSE");
        }
        setPresetType("WEEK");
        const defaultDates = getPresetDates("WEEK", "CURRENT");
        setPeriods([{...defaultDates, presetRange: "CURRENT"}]);
        setSelectedCategoryIds([]);
        setSelectedWalletIds([]);
        setSelectedBudgetIds([]);
        if (onReset) {
            onReset();
        }
    }, [onReportTypeChange, onReset, reportTypeProp]);

    useEffect(() => {
        if (!includeReportTypeFilter) return;
        setSelectedCategoryIds([]);
    }, [includeReportTypeFilter, reportType]);

    const handleReportTypeChange = useCallback((nextValue) => {
        if (onReportTypeChange) {
            onReportTypeChange(nextValue);
        }
        if (reportTypeProp === undefined) {
            setReportTypeState(nextValue);
        }
    }, [onReportTypeChange, reportTypeProp]);

    const updatePeriod = useCallback((index, key, value) => {
        setPeriods((prev) =>
            prev.map((period, idx) =>
                idx === index ? {...period, [key]: value} : period
            )
        );
    }, []);

    const addPeriod = useCallback(() => {
        setPeriods((prev) => {
            if (prev.length === 0) {
                const defaultDates = getPresetDates("WEEK", "CURRENT");
                return [{...defaultDates, presetRange: "CURRENT"}];
            }
            if (presetType !== "CUSTOM") {
                const options = presetType === "MONTH" ? MONTH_RANGE_OPTIONS : RANGE_OPTIONS;
                const lastRange = prev[prev.length - 1]?.presetRange || "CURRENT";
                const currentIndex = options.findIndex((option) => option.value === lastRange);
                const nextIndex = currentIndex >= 0 ? currentIndex + 1 : 1;
                const nextRange = nextIndex < options.length ? options[nextIndex].value : lastRange;
                const dates = getPresetDates(presetType, nextRange);
                return [...prev, {...dates, presetRange: nextRange}];
            }
            const earliest = prev[prev.length - 1];
            const fromDate = parseDate(earliest.dateFrom);
            const toDate = parseDate(earliest.dateTo);
            const dateFrom = fromDate ? formatDate(addDays(fromDate, -7)) : "";
            const dateTo = toDate ? formatDate(addDays(toDate, -7)) : "";
            return [...prev, {dateFrom, dateTo, presetRange: null}];
        });
    }, [presetType]);

    const removePeriod = useCallback((index) => {
        setPeriods((prev) => prev.filter((_, idx) => idx !== index));
    }, []);

    const resolvePreviousYearBaseRange = useCallback((currentPeriods, index) => {
        for (let i = index - 1; i >= 0; i -= 1) {
            const rangeValue = currentPeriods[i]?.presetRange;
            if (rangeValue && rangeValue !== "PREVIOUS_YEAR") {
                return rangeValue;
            }
        }
        return "CURRENT";
    }, []);

    useEffect(() => {
        if (presetType === "CUSTOM") return;
        setPeriods((prev) =>
            prev.map((period, index) => {
                let nextRange = period.presetRange || "CURRENT";
                if (presetType === "WEEK" && nextRange === "PREVIOUS_YEAR") {
                    nextRange = "CURRENT";
                }
                if (presetType === "MONTH" && nextRange === "PREVIOUS_YEAR") {
                    const baseRange = resolvePreviousYearBaseRange(prev, index);
                    const dates = getMonthRangeForRange(baseRange, -1);
                    return {...period, ...dates, presetRange: nextRange};
                }
                const dates = getPresetDates(presetType, nextRange);
                return {...period, ...dates, presetRange: nextRange};
            })
        );
    }, [presetType, resolvePreviousYearBaseRange]);

    const updatePresetRange = useCallback((index, presetRange) => {
        setPeriods((prev) =>
            prev.map((period, idx) => {
                if (idx !== index) return period;
                if (presetType === "MONTH" && presetRange === "PREVIOUS_YEAR") {
                    const baseRange = resolvePreviousYearBaseRange(prev, index);
                    const dates = getMonthRangeForRange(baseRange, -1);
                    return {...period, ...dates, presetRange};
                }
                const dates = getPresetDates(presetType, presetRange);
                return {...period, ...dates, presetRange};
            })
        );
    }, [presetType, resolvePreviousYearBaseRange]);

    useEffect(() => {
        if (presetType !== "MONTH") return;
        setPeriods((prev) => {
            let changed = false;
            const next = prev.map((period, index) => {
                if (period.presetRange !== "PREVIOUS_YEAR") return period;
                const baseRange = resolvePreviousYearBaseRange(prev, index);
                const dates = getMonthRangeForRange(baseRange, -1);
                if (period.dateFrom === dates.dateFrom && period.dateTo === dates.dateTo) {
                    return period;
                }
                changed = true;
                return {...period, ...dates};
            });
            return changed ? next : prev;
        });
    }, [presetType, periods, resolvePreviousYearBaseRange]);

    return (
        <Card sx={{maxWidth: 1000, margin: "0 auto 16px"}}>
            {showTypeTabs && (
                <CardHeader
                    sx={{pb: 0}}
                    title={
                        <Tabs
                            value={reportType}
                            onChange={(_, newValue) => handleReportTypeChange(newValue)}
                            aria-label={t("common.labels.reportType")}
                            variant="fullWidth"
                        >
                            <Tab label={t("common.transactionType.expense")} value="EXPENSE" />
                            <Tab label={t("common.transactionType.income")} value="INCOME" />
                        </Tabs>
                    }
                />
            )}
            <CardContent>
                <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 2}}>
                        <TextField
                            select
                            label={t("common.labels.period")}
                            value={presetType}
                            onChange={(event) => setPresetType(event.target.value)}
                            sx={{minWidth: 160}}
                            variant="standard"
                        >
                            {PRESET_TYPES.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {t(option.labelKey)}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    {periods.map((period, index) => (
                        <Box key={`period-${index}`} sx={{display: "flex", flexWrap: "wrap", gap: 2}}>
                            {presetType !== "CUSTOM" && (
                                <TextField
                                    select
                                    label={t("common.labels.range")}
                                    value={period.presetRange || "CURRENT"}
                                    onChange={(event) => updatePresetRange(index, event.target.value)}
                                    sx={{minWidth: 160}}
                                    variant="standard"
                                >
                                    {(presetType === "MONTH" ? MONTH_RANGE_OPTIONS : RANGE_OPTIONS).map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {t(option.labelKey)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}
                            <TextField
                                label={t("common.labels.from")}
                                type="date"
                                value={period.dateFrom}
                                onChange={(event) => updatePeriod(index, "dateFrom", event.target.value)}
                                disabled={presetType !== "CUSTOM"}
                                sx={{minWidth: 180}}
                                InputLabelProps={{shrink: true}}
                                variant="standard"
                            />
                            <TextField
                                label={t("common.labels.to")}
                                type="date"
                                value={period.dateTo}
                                onChange={(event) => updatePeriod(index, "dateTo", event.target.value)}
                                disabled={presetType !== "CUSTOM"}
                                sx={{minWidth: 180}}
                                InputLabelProps={{shrink: true}}
                                variant="standard"
                            />
                            <IconButton
                                disabled={periods.length === 1}
                                onClick={() => removePeriod(index)}
                                sx={{alignSelf: "center", color: "text.secondary"}}
                            >
                                <Delete />
                            </IconButton>
                        </Box>
                    ))}
                    <Box>
                        <Button variant="outlined" onClick={addPeriod}>
                            {t("common.buttons.addPeriod")}
                        </Button>
                    </Box>
                    {showCategories && (
                        <CategorySelect
                            multiple
                            selectedCategoryIds={selectedCategoryIds}
                            categoryType={categoryType || reportType}
                            label={t("common.labels.categories")}
                            width="260px"
                            onChange={(ids) => setSelectedCategoryIds(ids || [])}
                        />
                    )}
                    {showWallets && (
                        <Autocomplete
                            multiple
                            options={wallets || []}
                            value={selectedWallets}
                            onChange={(event, newValues) =>
                                setSelectedWalletIds(newValues.map((wallet) => wallet.id))
                            }
                            getOptionLabel={(wallet) => wallet?.name || ""}
                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t("common.labels.wallets")}
                                    variant="standard"
                                    sx={{minWidth: 260}}
                                    inputProps={{
                                        ...params.inputProps,
                                        autoComplete: "off"
                                    }}
                                />
                            )}
                        />
                    )}
                    {showBudgets && (
                        <Autocomplete
                            multiple
                            options={budgets || []}
                            value={selectedBudgets}
                            onChange={(event, newValues) =>
                                setSelectedBudgetIds(newValues.map((budget) => budget.id))
                            }
                            getOptionLabel={(budget) => budget?.name || ""}
                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t("common.labels.budgets")}
                                    variant="standard"
                                    sx={{minWidth: 260}}
                                    inputProps={{
                                        ...params.inputProps,
                                        autoComplete: "off"
                                    }}
                                />
                            )}
                        />
                    )}
                </Box>
                <Stack direction="row" spacing={2} sx={{mt: 2}}>
                    <Button variant="contained" onClick={handleApply}>
                        {t("common.buttons.apply")}
                    </Button>
                    <Button variant="outlined" onClick={handleReset}>
                        {t("common.buttons.reset")}
                    </Button>
                </Stack>
            </CardContent>
        </Card>
    );
};

export default ReportFilters;
